import { IconButton, Tooltip, Badge, SvgIconProps, ChipProps, CircularProgress } from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import BlockIcon from '@mui/icons-material/Block';
import ConfigUtils from "../../../utils/Config";

interface EstadoFacturacionIconProps {
  estado: EstadoFacturacion;
  onClick: () => void;
}

interface EstadoColorUnified {
  chip: ChipProps["color"];
  icon: SvgIconProps["color"];
}

export enum EstadoFacturacion {
  LOADING = "loading",
  SIN_FACTURA = "sin_factura",
  PENDIENTE_PAGO = "pendiente_pago",
  PAGADA = "pagada",
  EMITIDA = "emitida",
  EMITIDA_PENDIENTE_PAGO = "emitida_pendiente_pago",
  NO_FACTURABLE = "no_facturable",
  ANULADA = "anulada",
  ANULACION = "anulacion",
}

enum EstadoColorChip {
  DEFAULT = "default",
  ERROR = "error",
  SUCCESS = "success",
  WARNING = "warning",
}

enum EstadoColorIcon {
  DEFAULT = "inherit",
  ERROR = "error",
  SUCCESS = "success",
  WARNING = "warning",
}

export const EstadoFacturacionToColorUnified: Record<EstadoFacturacion, EstadoColorUnified> = {
  [EstadoFacturacion.LOADING]: {chip: EstadoColorChip.DEFAULT, icon: EstadoColorIcon.DEFAULT},
  [EstadoFacturacion.SIN_FACTURA]: {chip: EstadoColorChip.DEFAULT, icon: EstadoColorIcon.DEFAULT},
  [EstadoFacturacion.PENDIENTE_PAGO]: {chip: EstadoColorChip.ERROR, icon: EstadoColorIcon.ERROR},
  [EstadoFacturacion.PAGADA]: {chip: EstadoColorChip.SUCCESS, icon: EstadoColorIcon.SUCCESS},
  [EstadoFacturacion.EMITIDA]: {chip: EstadoColorChip.SUCCESS, icon: EstadoColorIcon.SUCCESS},
  [EstadoFacturacion.EMITIDA_PENDIENTE_PAGO]: {chip: EstadoColorChip.ERROR, icon: EstadoColorIcon.ERROR},
  [EstadoFacturacion.NO_FACTURABLE]: {chip: EstadoColorChip.WARNING, icon: EstadoColorIcon.WARNING},
  [EstadoFacturacion.ANULADA]: {chip: EstadoColorChip.DEFAULT, icon: EstadoColorIcon.DEFAULT},
  [EstadoFacturacion.ANULACION]: {chip: EstadoColorChip.DEFAULT, icon: EstadoColorIcon.DEFAULT},
};

const EstadoFacturacionToTooltip: Record<EstadoFacturacion, string> = {
  [EstadoFacturacion.LOADING]: "Cargando estado de facturación...",
  [EstadoFacturacion.SIN_FACTURA]: "Facturar cita",
  [EstadoFacturacion.PENDIENTE_PAGO]: "Factura pendiente de pago",
  [EstadoFacturacion.PAGADA]: "Factura pagada, pendiente de emitir",
  [EstadoFacturacion.EMITIDA]: "Factura emitida",
  [EstadoFacturacion.EMITIDA_PENDIENTE_PAGO]: "Factura emitida, pendiente de cobro",
  [EstadoFacturacion.NO_FACTURABLE]: "Cita marcada como no facturable",
  [EstadoFacturacion.ANULADA]: "Factura anulada",
  [EstadoFacturacion.ANULACION]: "Factura de anulación (cancela a otra factura)",
};

// La rectificativa por anulación se muestra siempre como "anulación" (gris), nunca como pendiente de cobro ni cobrada.
export const getEstadoFacturacion = (estado?: string, estadoPago?: string, tipoRectificacion?: string | null): EstadoFacturacion => {
  if (!estado) return EstadoFacturacion.SIN_FACTURA;
  if (tipoRectificacion === "anulacion" && estado === "Emitida") return EstadoFacturacion.ANULACION;
  if (estado === "Anulada") return EstadoFacturacion.ANULADA;
  if (estado === "Emitida" && estadoPago === "Pagada") return EstadoFacturacion.EMITIDA;
  if (estado === "Emitida") return EstadoFacturacion.EMITIDA_PENDIENTE_PAGO;
  if (estado === "Borrador" && estadoPago === "Pagada") return EstadoFacturacion.PAGADA;
  if (estado === "Borrador" && estadoPago === "Pendiente") return EstadoFacturacion.PENDIENTE_PAGO;
  return EstadoFacturacion.SIN_FACTURA;
};

// Texto con el que se muestra el estado en los chips de los listados.
export const etiquetaEstadoFacturacion = (estado: string): string =>
  estado === EstadoFacturacion.ANULACION ? "Anulación" : estado;

const EstadoFacturacionIcon = ({
  estado,
  onClick,
}: EstadoFacturacionIconProps) => {
  const color = EstadoFacturacionToColorUnified[estado].icon;
  const showDot = estado === EstadoFacturacion.PAGADA || estado === EstadoFacturacion.PENDIENTE_PAGO;
  const isFacturacionReady = ConfigUtils.isFacturacionReady();

  const icon = estado === EstadoFacturacion.LOADING ? (
    <CircularProgress size={24} color="inherit" />
  ) : showDot ? (
    <Badge
      variant="dot"
      color="warning"
      overlap="circular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <EuroIcon color={color} />
    </Badge>
  ) : estado === EstadoFacturacion.ANULADA || estado === EstadoFacturacion.ANULACION ? (
    <BlockIcon color={color} />
  ) : (
    <EuroIcon color={color} />
  );

  const tooltipText = isFacturacionReady
    ? EstadoFacturacionToTooltip[estado]
    : "Debes revisar la serie y el número de facturación antes de realizar acciones.";

  return (
    <Tooltip title={tooltipText} placement="top">
      <span>
        <IconButton onClick={onClick} disabled={!isFacturacionReady || estado === EstadoFacturacion.LOADING}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default EstadoFacturacionIcon;